// WHAT: Page component for time tracking with manual entry support
// WHY:  Provides interface for tracking time and manually entering time entries
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: TrackingTab, TimeTracQContext, useDataFetcher, useDailyPlan, useSupabaseTasks, ManualEntryDialog
// RISKS:
//   - NOTE: Manual entry save logic is not yet implemented
//   - NOTE: Stop tracking handler is empty - may not work correctly
//   - NOTE: Context dependencies may cause re-renders

import { useState, useEffect, memo } from "react";

import { debugProductivity } from "@/domains/logging/utils/debugLog";

import {
  useTimeTracQContext,
  useTimeTracQLiveContext,
} from "../../contexts/TimeTracQContext.shared";
import { useSupabaseTasks } from "../../hooks/data/useSupabasetasks";
import { useDailyPlan } from "../../hooks/useDailyPlan";
import { useDataFetcher } from "../../hooks/useDataFetcher";
import { TrackingTab } from "../../screen/tabs/TrackingTab";
import { ManualEntryDialog } from "../ManualEntryDialog";

/**
 * WHAT:
 *   Page component that provides time tracking interface with manual entry
 *   dialog and real-time tracking state management.
 *
 * WHY:
 *   Enables users to track time actively or manually enter time entries
 *   with comprehensive data display and controls.
 *
 * BEHAVIOR:
 *   - Uses TimeTracQ context for tracking state
 *   - Loads tasks and calculates data (shortfalls, daily plan, overview)
 *   - Manages manual entry dialog state
 *   - Displays tracking interface with proximity state
 *   - Handles manual time entry (save logic not yet implemented)
 *
 * RISKS:
 *   - NOTE: Manual entry save logic is not yet implemented
 *   - NOTE: Stop tracking handler depends on context - may not work correctly
 *   - NOTE: Context dependencies may cause re-renders
 *
 * @returns {JSX.Element} The tracking page component
 *
 */
const TrackingPageComponent = () => {
  // NOTE: Use context directly - it should be available since we're within TimeTracQProvider
  const contextData = useTimeTracQContext();

  const {
    config,
    confirmedWorkspace,
    currentOptions,
    currentSelectionIndex,
    isLoading,
    isTracking,
    mode,
    session,
    stopTracking,
  } = contextData;

  // NOTE: Debug logging for component lifecycle
  useEffect(() => {
    debugProductivity("info", "TrackingPage", "Component mounted", {
      currentOptionsCount: currentOptions.length,
      hasSession: !!session,
      isTracking,
      mode,
    });
  }, [isTracking, session, mode, currentOptions]);

  useEffect(() => {
    debugProductivity("info", "TrackingPage", "Tracking state changed", {
      aspect: session?.aspect,
      isTracking,
      session_id: session?.id,
      workspace: session?.workspace,
    });
  }, [isTracking, session]);

  const { lastHeartbeat, proximityState } = useTimeTracQLiveContext();

  const [showManualModal, setShowManualModal] = useState(false);
  const [shortfallViewMode] = useState<"full" | "today">("today");

  // Manual entry modal state
  const [manualWorkspace, setManualWorkspace] = useState("");
  const [manualAspect, setManualAspect] = useState("");
  const [manualStart, setManualStart] = useState("");
  const [manualDuration, setManualDuration] = useState("");
  const [manualNotes, setManualNotes] = useState("");

  // Load tasks and data
  const { tasks } = useSupabaseTasks();

  const { expectedTotal, onTrackFactor, shortfalls, workedToday, workedTotal } = useDataFetcher({
    config: config as Record<string, Record<string, { color: string; hours: number }>>,
    shortfallViewMode,
    tasks,
  });

  const { dailyCapacity } = useDailyPlan({
    config: config as Record<string, Record<string, { color: string; hours: number }>>,
    shortfalls,
    shortfallViewMode,
    tasks,
  });

  const trackingProperties = {
    confirmedWorkspace: confirmedWorkspace ?? "",
    currentOptions,
    currentSelectionIndex,
    dailyCapacity,
    expectedTotal,
    handleStopTracking: () => {
      try {
        stopTracking();
      } catch {
        // NOTE: Errors are handled inside useTimeTracQContext implementation
      }
    },
    isTracking,
    lastHeartbeat,
    mode,
    onTrackFactor: onTrackFactor ?? null,
    proximityState,
    session,
    setShowManualModal,
    workedToday,
    workedTotal,
  };

  const handleManualSave = () => {
    if (!manualWorkspace || !manualAspect || !manualStart || !manualDuration) {
      alert("Bitte alle Felder ausfüllen");
      return;
    }
    try {
      // NOTE: Manual entry save logic is not yet implemented
      setShowManualModal(false);
      // NOTE: Reset form
      setManualWorkspace("");
      setManualAspect("");
      setManualStart("");
      setManualDuration("");
      setManualNotes("");
    } catch {
      // NOTE: Error saving manual entry
      alert("Fehler beim Speichern der Eingabe.");
    }
  };

  if (isLoading) {
    return <div className="text-white p-6">🔄 Lade Workspace-Konfiguration...</div>;
  }

  return (
    <>
      <TrackingTab {...trackingProperties} />
      <ManualEntryDialog
        aspectOptions={manualWorkspace ? Object.keys(config[manualWorkspace] ?? {}) : []}
        duration={manualDuration}
        efficiencyRating={null}
        impactScore=""
        notes={manualNotes}
        onAspectChange={setManualAspect}
        onClose={() => {
          setShowManualModal(false);
        }}
        onDurationChange={setManualDuration}
        onEfficiencyRatingChange={() => {
          // NOTE: Placeholder - to be implemented
        }}
        onImpactScoreChange={() => {
          // NOTE: Placeholder - to be implemented
        }}
        onNotesChange={setManualNotes}
        onSave={handleManualSave}
        onStartChange={setManualStart}
        onWhatWasInefficientChange={() => {
          // NOTE: Placeholder - to be implemented
        }}
        onWhatWentWellChange={() => {
          // NOTE: Placeholder - to be implemented
        }}
        onWorkspaceChange={setManualWorkspace}
        open={showManualModal}
        selectedAspect={manualAspect}
        selectedWorkspace={manualWorkspace}
        start={manualStart}
        whatWasInefficient=""
        whatWentWell=""
        workspaceOptions={Object.keys(config).filter(
          (k) => !["Settings", "Meta", "System", "Version"].includes(k),
        )}
      />
    </>
  );
};

// ... existing code ...

export const TrackingPage = memo(TrackingPageComponent);
